import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, CheckCircle2, FileCheck2, Gift, Info, Loader2, Lock } from 'lucide-react';
import { Card, CardContent } from '@components/Card';
import { Button } from '@components/Button';
import { Dialog } from '@components/Dialog';
import { PermissionGuard } from '@components/PermissionGuard';
import demoReconciliationService from '@services/demoReconciliationService';
import {
  DemoBalance,
  DemoConfirmResult,
  DemoOrder,
  DemoReceiptItem,
  DemoSelection,
  DemoWorkspace,
  demoStatusLabel,
} from '@/types/demoReconciliation';
import { formatCurrencyBRL, formatDateBR, formatQuantityBR } from '@pages/financial/financialUtils';
import { CopyLink } from './CopyLink';

/** Escolha do usuário para um item de recebimento. */
interface ItemChoice {
  use: boolean;
  order_id: number | null;
}

const PREVIEW_DEBOUNCE_MS = 300;

const formatPercent = (v: number): string =>
  `${(v ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;

/** Pedidos do workspace que contêm o SKU do item recebido. */
const ordersWithSku = (orders: DemoOrder[], sku: string): DemoOrder[] =>
  orders.filter((o) => o.items.some((i) => i.sku === sku));

/** Estado inicial: vínculo gravado manda; depois a sugestão do backend. */
const initialChoices = (ws: DemoWorkspace): Record<number, ItemChoice> => {
  const out: Record<number, ItemChoice> = {};
  for (const r of ws.receipts) {
    for (const it of r.items) {
      if (it.order_id != null) {
        out[it.material_receipt_item_id] = { use: true, order_id: it.order_id };
        continue;
      }
      const options = ordersWithSku(ws.orders, it.sku);
      const suggested =
        it.suggested_order_id != null && options.some((o) => o.order_id === it.suggested_order_id)
          ? it.suggested_order_id
          : null;
      out[it.material_receipt_item_id] = { use: suggested != null, order_id: suggested };
    }
  }
  return out;
};

/**
 * Conciliação de Demonstração — área de trabalho de uma cliente.
 *
 * O saldo (o que ficou com a cliente) é calculado SÓ pelo backend: a cada
 * mudança de seleção chamamos o preview (debounce de 300 ms) e exibimos o que
 * voltar. O front nunca soma quantidades nem valores.
 */
export const DemoReconciliationWorkspaceView: React.FC = () => {
  const { customerId: customerIdParam } = useParams<{ customerId: string }>();
  const customerId = Number(customerIdParam);
  const navigate = useNavigate();

  const [workspace, setWorkspace] = useState<DemoWorkspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [choices, setChoices] = useState<Record<number, ItemChoice>>({});

  const [balance, setBalance] = useState<DemoBalance | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewSeq = useRef(0);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [result, setResult] = useState<DemoConfirmResult | null>(null);

  // ── Carga do workspace ─────────────────────────────────────────
  useEffect(() => {
    if (!Number.isFinite(customerId) || customerId <= 0) {
      setError('Cliente inválida.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const ws = await demoReconciliationService.getWorkspace(customerId);
        if (cancelled) return;
        setWorkspace(ws);
        setChoices(initialChoices(ws));
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Erro ao carregar a conciliação');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  const selections: DemoSelection[] = useMemo(
    () =>
      Object.entries(choices)
        .filter(([, c]) => c.use && c.order_id != null)
        .map(([id, c]) => ({ material_receipt_item_id: Number(id), order_id: c.order_id as number })),
    [choices],
  );

  // ── Preview (debounced) ────────────────────────────────────────
  const runPreview = useCallback(
    async (sel: DemoSelection[]) => {
      const seq = ++previewSeq.current;
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const data = await demoReconciliationService.preview(customerId, { selections: sel });
        if (seq !== previewSeq.current) return;
        setBalance(data);
      } catch (err) {
        if (seq !== previewSeq.current) return;
        setBalance(null);
        setPreviewError(err instanceof Error ? err.message : 'Erro ao calcular o saldo');
      } finally {
        if (seq === previewSeq.current) setPreviewLoading(false);
      }
    },
    [customerId],
  );

  useEffect(() => {
    if (!workspace || result) return;
    const t = setTimeout(() => runPreview(selections), PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [workspace, selections, runPreview, result]);

  // ── Ações ──────────────────────────────────────────────────────
  const setChoice = (itemId: number, patch: Partial<ItemChoice>) =>
    setChoices((prev) => ({ ...prev, [itemId]: { ...prev[itemId], ...patch } }));

  /** Recebimentos que terão ao menos um item usado — serão marcados como conciliados. */
  const receiptsToConciliate = useMemo(() => {
    if (!workspace) return [];
    const usedIds = new Set(selections.map((s) => s.material_receipt_item_id));
    return workspace.receipts.filter((r) => r.items.some((i) => usedIds.has(i.material_receipt_item_id)));
  }, [workspace, selections]);

  const doConfirm = async () => {
    setConfirming(true);
    setConfirmError(null);
    try {
      const res = await demoReconciliationService.confirm(customerId, { selections });
      setResult(res);
      setConfirmOpen(false);
    } catch (err) {
      setConfirmError(err instanceof Error ? err.message : 'Erro ao confirmar a conciliação');
    } finally {
      setConfirming(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────
  const backLink = (
    <Link
      to="/demo-reconciliation"
      className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
    >
      <ArrowLeft size={16} /> Voltar para a lista
    </Link>
  );

  let body: React.ReactNode;
  if (loading) {
    body = (
      <div className="flex items-center justify-center py-16 text-slate-500 text-sm gap-2">
        <Loader2 size={18} className="animate-spin" /> Carregando…
      </div>
    );
  } else if (error || !workspace) {
    body = (
      <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
        {error ?? 'Conciliação não encontrada.'}
      </div>
    );
  } else if (result) {
    body = <ResultPanel result={result} onBack={() => navigate('/demo-reconciliation')} />;
  } else {
    const hasBalance = balance?.has_balance ?? false;
    body = (
      <>
        {/* Cabeçalho */}
        <Card>
          <CardContent>
            <div className="flex flex-wrap gap-x-10 gap-y-3">
              <div>
                <p className="text-xs text-slate-500">Cliente</p>
                <p className="font-semibold text-slate-900">{workspace.customer_name}</p>
                {workspace.document && <p className="text-xs text-slate-500">{workspace.document}</p>}
              </div>
              <div>
                <p className="text-xs text-slate-500">Vendedora (mantida na proposta)</p>
                <p className="font-semibold text-slate-900">
                  {balance?.seller_name || workspace.seller_name || '—'}
                </p>
              </div>
            </div>
            <div className="mt-4 flex items-start gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3">
              <Info size={16} className="text-slate-500 flex-shrink-0 mt-0.5" />
              <span>
                O frete dos pedidos de demonstração não é cobrado — o frete é calculado na nova proposta pelo CRM.
              </span>
            </div>
          </CardContent>
        </Card>

        {/* 1. Pedidos de demonstração */}
        <section>
          <h2 className="text-h2 mb-4">Pedidos de demonstração</h2>
          {workspace.orders.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhum pedido de demonstração pendente.</p>
          ) : (
            <div className="space-y-4">
              {workspace.orders.map((o) => (
                <OrderCard key={o.order_id} order={o} />
              ))}
            </div>
          )}
        </section>

        {/* 2. Recebimentos */}
        <section>
          <h2 className="text-h2 mb-2">Recebimentos de materiais disponíveis para conciliação</h2>
          <p className="text-sm text-slate-600 mb-4">
            Marque os itens devolvidos que abatem os pedidos de demonstração. Ao confirmar, o pedido escolhido
            será gravado no item do recebimento, e os recebimentos conciliados não aparecem em futuras
            conciliações.
          </p>
          {workspace.receipts.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhum recebimento disponível para esta cliente.</p>
          ) : (
            <div className="space-y-4">
              {workspace.receipts.map((r) => (
                <Card key={r.material_receipt_id}>
                  <CardContent className="p-0">
                    <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                      <Link
                        to={`/material-receipts/${r.material_receipt_id}`}
                        className="font-semibold text-blue-700 hover:underline"
                      >
                        Recebimento #{r.material_receipt_id}
                      </Link>
                      <span className="text-slate-600">{r.receipt_date ? formatDateBR(r.receipt_date) : 'sem data'}</span>
                      {r.notes && <span className="text-slate-500 italic">{r.notes}</span>}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600">
                          <tr>
                            <th className="text-center font-medium px-4 py-2 w-16">Usar</th>
                            <th className="text-left font-medium px-4 py-2">SKU</th>
                            <th className="text-left font-medium px-4 py-2">Descrição</th>
                            <th className="text-right font-medium px-4 py-2">Qtd</th>
                            <th className="text-left font-medium px-4 py-2">Pedido</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {r.items.map((it) => (
                            <ReceiptItemRow
                              key={it.material_receipt_item_id}
                              item={it}
                              orders={workspace.orders}
                              choice={choices[it.material_receipt_item_id] ?? { use: false, order_id: null }}
                              onChange={(patch) => setChoice(it.material_receipt_item_id, patch)}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* 3. Saldo */}
        <section>
          <h2 className="text-h2 mb-4 flex items-center gap-2">
            Saldo com a cliente (será cobrado)
            {previewLoading && <Loader2 size={18} className="animate-spin text-slate-400" />}
          </h2>
          <BalanceCard balance={balance} error={previewError} />
        </section>

        <div className="flex justify-end">
          <PermissionGuard permission="orders.demo_reconciliation">
            <Button
              variant={hasBalance ? 'primary' : 'secondary'}
              disabled={!balance || previewLoading || !!previewError}
              onClick={() => {
                setConfirmError(null);
                setConfirmOpen(true);
              }}
              icon={<FileCheck2 />}
            >
              {hasBalance ? 'Gerar proposta no CRM' : 'Encerrar sem cobrança'}
            </Button>
          </PermissionGuard>
        </div>

        <Dialog
          isOpen={confirmOpen}
          onClose={() => !confirming && setConfirmOpen(false)}
          title={hasBalance ? 'Gerar proposta no CRM' : 'Encerrar sem cobrança'}
          size="lg"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={confirming}>
                Voltar
              </Button>
              <Button variant={hasBalance ? 'primary' : 'danger'} onClick={doConfirm} loading={confirming}>
                Confirmar
              </Button>
            </div>
          }
        >
          <div className="space-y-3 text-sm text-slate-700">
            {hasBalance ? (
              <p>
                Será gerada uma proposta no CRM para <strong>{workspace.customer_name}</strong> no valor de{' '}
                <strong>{formatCurrencyBRL(balance?.total ?? 0)}</strong> (frete calculado pelo CRM).
              </p>
            ) : (
              <p>
                Não há saldo a cobrar de <strong>{workspace.customer_name}</strong>. A conciliação será encerrada
                sem cobrança.
              </p>
            )}
            <div>
              <p className="font-medium text-red-700">
                Os pedidos de demonstração abaixo serão CANCELADOS:
              </p>
              <ul className="list-disc pl-5 mt-1">
                {workspace.orders.map((o) => (
                  <li key={o.order_id}>
                    Pedido {o.order_id} — {formatDateBR(o.order_date)}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-medium">Os recebimentos abaixo serão marcados como conciliados:</p>
              {receiptsToConciliate.length === 0 ? (
                <p className="text-slate-500">Nenhum recebimento selecionado.</p>
              ) : (
                <ul className="list-disc pl-5 mt-1">
                  {receiptsToConciliate.map((r) => (
                    <li key={r.material_receipt_id}>
                      Recebimento #{r.material_receipt_id}
                      {r.receipt_date ? ` — ${formatDateBR(r.receipt_date)}` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {confirmError && (
              <div className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700">{confirmError}</div>
            )}
          </div>
        </Dialog>
      </>
    );
  }

  return (
    <PermissionGuard permission="orders.demo_reconciliation" showError>
      <div className="space-y-6">
        <div>
          {backLink}
          <h1 className="text-h1 mt-2">Conciliação de Demonstração</h1>
        </div>
        {body}
      </div>
    </PermissionGuard>
  );
};

// ─── Pedido de demonstração ─────────────────────────────────────

const OrderCard: React.FC<{ order: DemoOrder }> = ({ order }) => (
  <Card>
    <CardContent className="p-0">
      <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <Link to={`/sales-orders/${order.order_id}`} className="font-semibold text-blue-700 hover:underline">
          Pedido {order.order_id}
        </Link>
        <span className="text-slate-600">{formatDateBR(order.order_date)}</span>
        <span className="inline-flex px-2 py-0.5 rounded-full border text-xs font-medium bg-slate-100 text-slate-700 border-slate-200">
          {order.order_status}
        </span>
        {order.seller_name && <span className="text-slate-500">Vendedora: {order.seller_name}</span>}
        {order.proposal_num != null && <span className="text-slate-500">Proposta {order.proposal_num}</span>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left font-medium px-4 py-2">SKU</th>
              <th className="text-left font-medium px-4 py-2">Descrição</th>
              <th className="text-right font-medium px-4 py-2">Qtd</th>
              <th className="text-right font-medium px-4 py-2">Devolvida já registrada</th>
              <th className="text-right font-medium px-4 py-2">Preço unit.</th>
              <th className="text-right font-medium px-4 py-2">Desc. %</th>
              <th className="text-right font-medium px-4 py-2">Líquido unit.</th>
              <th className="text-right font-medium px-4 py-2">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {order.items.map((it) => (
              <tr key={it.order_item_id}>
                <td className="px-4 py-2 text-slate-700 whitespace-nowrap">{it.sku}</td>
                <td className="px-4 py-2 text-slate-800">
                  {it.description}
                  {it.is_gift && <GiftBadge />}
                </td>
                <td className="px-4 py-2 text-right">{formatQuantityBR(it.quantity)}</td>
                <td className="px-4 py-2 text-right">{formatQuantityBR(it.returned_quantity)}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">{formatCurrencyBRL(it.unit_price)}</td>
                <td className="px-4 py-2 text-right">{formatPercent(it.discount_percent)}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">{formatCurrencyBRL(it.net_unit_price)}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  {/* Total da linha enviada = líquido unitário × quantidade (exibição, não é o saldo). */}
                  {formatCurrencyBRL(it.net_unit_price * it.quantity)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
);

const GiftBadge: React.FC = () => (
  <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium bg-pink-50 text-pink-700 border-pink-200">
    <Gift size={12} /> Brinde
  </span>
);

// ─── Item de recebimento ────────────────────────────────────────

const ReceiptItemRow: React.FC<{
  item: DemoReceiptItem;
  orders: DemoOrder[];
  choice: ItemChoice;
  onChange: (patch: Partial<ItemChoice>) => void;
}> = ({ item, orders, choice, onChange }) => {
  const locked = item.order_id != null;
  const options = ordersWithSku(orders, item.sku);
  const noOption = !locked && options.length === 0;

  return (
    <tr className={choice.use ? 'bg-emerald-50/40' : ''}>
      <td className="px-4 py-2 text-center">
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={choice.use}
          disabled={noOption || choice.order_id == null}
          onChange={(e) => onChange({ use: e.target.checked })}
          title={noOption ? 'Sem pedido de demonstração com este produto' : undefined}
        />
      </td>
      <td className="px-4 py-2 text-slate-700 whitespace-nowrap">{item.sku}</td>
      <td className="px-4 py-2 text-slate-800">{item.description}</td>
      <td className="px-4 py-2 text-right">{formatQuantityBR(item.quantity)}</td>
      <td className="px-4 py-2">
        {locked ? (
          <div className="flex items-center gap-2">
            <select className="input text-sm" value={item.order_id ?? ''} disabled>
              <option value={item.order_id ?? ''}>Pedido {item.order_id}</option>
            </select>
            <span className="inline-flex items-center gap-1 text-xs text-slate-500" title="Vínculo já gravado pela logística">
              <Lock size={12} /> vinculado
            </span>
          </div>
        ) : noOption ? (
          <span className="text-xs text-slate-500 italic">sem pedido de demonstração com este produto</span>
        ) : (
          <select
            className="input text-sm"
            value={choice.order_id ?? ''}
            onChange={(e) => {
              const val = e.target.value ? Number(e.target.value) : null;
              onChange({ order_id: val, use: val != null });
            }}
          >
            <option value="">Selecione o pedido…</option>
            {options.map((o) => (
              <option key={o.order_id} value={o.order_id}>
                Pedido {o.order_id} — {formatDateBR(o.order_date)}
              </option>
            ))}
          </select>
        )}
      </td>
    </tr>
  );
};

// ─── Saldo ──────────────────────────────────────────────────────

const Warnings: React.FC<{ warnings?: string[] }> = ({ warnings }) =>
  warnings && warnings.length > 0 ? (
    <div className="rounded-lg border p-3 bg-amber-50 border-amber-200">
      <div className="flex items-start gap-2">
        <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <ul className="text-sm text-amber-800 space-y-1">
          {warnings.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      </div>
    </div>
  ) : null;

const BalanceCard: React.FC<{ balance: DemoBalance | null; error: string | null }> = ({ balance, error }) => {
  if (error) {
    return <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>;
  }
  if (!balance) {
    return <p className="text-sm text-slate-500">Calculando saldo…</p>;
  }
  return (
    <div className="space-y-3">
      <Warnings warnings={balance.warnings} />
      <Card>
        <CardContent className="p-0">
          {balance.lines.length === 0 ? (
            <div className="p-4 text-sm text-slate-600">Nenhum produto ficou com a cliente.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left font-medium px-4 py-2">Pedido</th>
                    <th className="text-left font-medium px-4 py-2">SKU</th>
                    <th className="text-left font-medium px-4 py-2">Descrição</th>
                    <th className="text-right font-medium px-4 py-2">Enviada</th>
                    <th className="text-right font-medium px-4 py-2">Devolvida</th>
                    <th className="text-right font-medium px-4 py-2">Saldo</th>
                    <th className="text-right font-medium px-4 py-2">Preço unit.</th>
                    <th className="text-right font-medium px-4 py-2">Desc. %</th>
                    <th className="text-right font-medium px-4 py-2">Líquido unit.</th>
                    <th className="text-right font-medium px-4 py-2">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {balance.lines.map((l, i) => (
                    <tr key={`${l.order_id}-${l.sku}-${i}`} className={l.balance_quantity > 0 ? '' : 'text-slate-400'}>
                      <td className="px-4 py-2">{l.order_id}</td>
                      <td className="px-4 py-2 whitespace-nowrap">{l.sku}</td>
                      <td className="px-4 py-2">
                        {l.description}
                        {l.is_gift && <GiftBadge />}
                      </td>
                      <td className="px-4 py-2 text-right">{formatQuantityBR(l.sent_quantity)}</td>
                      <td className="px-4 py-2 text-right">{formatQuantityBR(l.returned_quantity)}</td>
                      <td className="px-4 py-2 text-right font-medium">{formatQuantityBR(l.balance_quantity)}</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">{formatCurrencyBRL(l.unit_price)}</td>
                      <td className="px-4 py-2 text-right">{formatPercent(l.discount_percent)}</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">{formatCurrencyBRL(l.net_unit_price)}</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">{formatCurrencyBRL(l.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="px-4 py-3 border-t border-slate-200 flex justify-end items-center gap-3 bg-slate-50">
            <span className="text-sm text-slate-600">Total a cobrar (sem frete)</span>
            <span className="text-lg font-semibold text-slate-900">{formatCurrencyBRL(balance.total)}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ─── Resultado ──────────────────────────────────────────────────

const ResultPanel: React.FC<{ result: DemoConfirmResult; onBack: () => void }> = ({ result, onBack }) => (
  <Card>
    <CardContent>
      <div className="flex items-start gap-3">
        <CheckCircle2 className="text-emerald-600 flex-shrink-0 mt-0.5" size={22} />
        <div className="flex-1 space-y-4">
          <div>
            <p className="font-semibold text-emerald-800">{demoStatusLabel(result.status)}</p>
            {result.comercial_proposal_id != null && (
              <p className="text-sm text-slate-700 mt-1">
                Proposta nº <strong>{result.comercial_proposal_id}</strong>
              </p>
            )}
          </div>

          {(result.payment_link || result.pix_link) && (
            <div className="space-y-2">
              <CopyLink label="Link de pagamento" value={result.payment_link} />
              <CopyLink label="Link PIX" value={result.pix_link} />
            </div>
          )}

          {result.status === 'proposal_created' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div className="rounded-lg border border-slate-200 p-3">
                <p className="text-xs text-slate-500">Produtos</p>
                <p className="font-semibold text-slate-900">{formatCurrencyBRL(result.products_total)}</p>
              </div>
              <div className="rounded-lg border border-slate-200 p-3">
                <p className="text-xs text-slate-500">Frete</p>
                <p className="font-semibold text-slate-900">{formatCurrencyBRL(result.shipping_price)}</p>
              </div>
              <div className="rounded-lg border border-slate-200 p-3">
                <p className="text-xs text-slate-500">Total da proposta</p>
                <p className="font-semibold text-slate-900">{formatCurrencyBRL(result.sell_total)}</p>
              </div>
            </div>
          )}

          {result.cancelled_order_ids?.length > 0 && (
            <div className="text-sm text-slate-700">
              <span className="font-medium">Pedidos cancelados: </span>
              {result.cancelled_order_ids.map((id, i) => (
                <React.Fragment key={id}>
                  {i > 0 && ', '}
                  <Link to={`/sales-orders/${id}`} className="text-blue-700 hover:underline">
                    {id}
                  </Link>
                </React.Fragment>
              ))}
            </div>
          )}

          <Warnings warnings={result.warnings} />

          <Button variant="outline" onClick={onBack} icon={<ArrowLeft />}>
            Voltar para a lista
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
);
